import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { AlertCircleIcon, BarChart3Icon, CheckSquareIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, CreditCardIcon, FileEditIcon, FileSpreadsheetIcon, FileTextIcon, LayoutDashboardIcon, ListIcon, MenuIcon, PlusIcon, ReceiptIcon, SettingsIcon, UsersIcon, Undo2Icon, XIcon } from 'lucide-react';
import { BillingDashboard } from './BillingDashboard';
import { Customers } from './Customers';
import { ItemList } from './ItemList';
import { Invoices } from './Invoices';
import { NewInvoice } from './NewInvoice';
import { Payments } from './Payments';
import { SalesReceipts } from './SalesReceipts';
import { Quotations } from './Quotations';
import { CreditNotes } from './CreditNotes';
import { Reports } from './Reports';
import { Settings } from './Settings';
export type BillingSection = 'dashboard' | 'customers' | 'new-invoice' | 'invoices-draft' | 'invoices-sent' | 'invoices-unpaid' | 'invoices-paid' | 'invoices-partial' | 'invoices-overdue' | 'quotations' | 'receipts' | 'payments' | 'sales-receipts' | 'credit-notes' | 'item-list' | 'reports' | 'settings';
type NavigationItem = {
  id: BillingSection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
};
type NavigationGroup = {
  label: string;
  items: NavigationItem[];
};
const sectionCopy: Record<BillingSection, {
  title: string;
  subtitle: string;
}> = {
  dashboard: {
    title: 'Billing overview',
    subtitle: 'Monitor collections, receivables, and today’s billing priorities.'
  },
  customers: {
    title: 'Customers',
    subtitle: 'Find registered patients or manage walk-in billing profiles.'
  },
  'new-invoice': {
    title: 'Create invoice',
    subtitle: 'Build a bill from the service catalogue or an active patient visit.'
  },
  'invoices-draft': {
    title: 'Draft invoices',
    subtitle: 'Review invoices before sending them to customers.'
  },
  'invoices-sent': {
    title: 'Sent invoices',
    subtitle: 'Track invoices that have been issued to customers.'
  },
  'invoices-unpaid': {
    title: 'Unpaid invoices',
    subtitle: 'Follow up invoices with an outstanding balance.'
  },
  'invoices-paid': {
    title: 'Paid invoices',
    subtitle: 'Review fully settled billing records.'
  },
  'invoices-partial': {
    title: 'Partially paid invoices',
    subtitle: 'Track invoices with a remaining balance.'
  },
  'invoices-overdue': {
    title: 'Overdue invoices',
    subtitle: 'Prioritize accounts that are past their due date.'
  },
  quotations: {
    title: 'Quotations',
    subtitle: 'Prepare and manage estimates before issuing an invoice.'
  },
  receipts: {
    title: 'Receipts',
    subtitle: 'View and issue payment receipts for collected balances.'
  },
  payments: {
    title: 'Payments received',
    subtitle: 'Record payments and keep invoice balances current.'
  },
  'sales-receipts': {
    title: 'Sales receipts',
    subtitle: 'Record independent non-invoice cash sales and issue receipts.'
  },
  'credit-notes': {
    title: 'Credit notes',
    subtitle: 'Manage billing corrections, refunds, and service credits.'
  },
  'item-list': {
    title: 'Service catalogue',
    subtitle: 'Maintain the approved billable services and pricing list.'
  },
  reports: {
    title: 'Reports',
    subtitle: 'Review financial activity across billing operations.'
  },
  settings: {
    title: 'Billing settings',
    subtitle: 'Configure billing preferences and operational controls.'
  }
};
export function BillingModule() {
  const {
    currentUser,
    invoices
  } = useData();
  const [activeSection, setActiveSection] = useState<BillingSection>('dashboard');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const invoiceCounts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      draft: invoices.filter(invoice => invoice.status === 'Draft').length,
      sent: invoices.filter(invoice => invoice.status === 'Sent').length,
      unpaid: invoices.filter(invoice => invoice.status === 'Unpaid').length,
      partial: invoices.filter(invoice => invoice.status === 'Partially Paid').length,
      paid: invoices.filter(invoice => invoice.status === 'Paid').length,
      overdue: invoices.filter(invoice => invoice.status === 'Overdue' || (invoice.status === 'Unpaid' || invoice.status === 'Partially Paid') && invoice.dueDate < today).length
    };
  }, [invoices]);
  if (!currentUser || currentUser.role !== 'Administrator' && currentUser.role !== 'Reception') {
    return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-1076" data-mp="159er5erog8h7">
        You do not have permission to access the Billing Module.
      </div>;
  }
  const isAdmin = currentUser.role === 'Administrator';
  const navigationGroups: NavigationGroup[] = [{
    label: 'Overview',
    items: [{
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }]
  }, {
    label: 'Sales',
    items: [{
      id: 'customers',
      label: 'Customers',
      icon: UsersIcon
    }, {
      id: 'new-invoice',
      label: 'New Invoice',
      icon: PlusIcon
    }, {
      id: 'quotations',
      label: 'Quotations',
      icon: FileSpreadsheetIcon
    }, {
      id: 'credit-notes',
      label: 'Credit Notes',
      icon: Undo2Icon
    }]
  }, {
    label: 'Invoices',
    items: [{
      id: 'invoices-draft',
      label: 'Draft',
      icon: FileEditIcon,
      count: invoiceCounts.draft
    }, {
      id: 'invoices-sent',
      label: 'Sent',
      icon: FileTextIcon,
      count: invoiceCounts.sent
    }, {
      id: 'invoices-unpaid',
      label: 'Unpaid',
      icon: AlertCircleIcon,
      count: invoiceCounts.unpaid
    }, {
      id: 'invoices-partial',
      label: 'Partially Paid',
      icon: ClockIcon,
      count: invoiceCounts.partial
    }, {
      id: 'invoices-paid',
      label: 'Paid',
      icon: CheckSquareIcon,
      count: invoiceCounts.paid
    }, {
      id: 'invoices-overdue',
      label: 'Overdue',
      icon: AlertCircleIcon,
      count: invoiceCounts.overdue
    }]
  }, {
    label: 'Cashflow',
    items: [{
      id: 'payments',
      label: 'Payments Received',
      icon: CreditCardIcon
    }, {
      id: 'receipts',
      label: 'Invoice Receipts',
      icon: ReceiptIcon
    }, {
      id: 'sales-receipts',
      label: 'Sales Receipts',
      icon: ReceiptIcon
    }]
  }, {
    label: 'Admin',
    items: [...(isAdmin ? [{
      id: 'item-list' as BillingSection,
      label: 'Item List',
      icon: ListIcon
    }, {
      id: 'reports' as BillingSection,
      label: 'Reports',
      icon: BarChart3Icon
    }, {
      id: 'settings' as BillingSection,
      label: 'Settings',
      icon: SettingsIcon
    }] : [])]
  }];
  const selectSection = (section: BillingSection) => {
    setActiveSection(section);
    setIsMobileNavOpen(false);
  };
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <BillingDashboard onNavigate={selectSection} data-id="element-1077" data-mp="3t8irslqsd1jg" />;
      case 'customers':
        return <Customers data-id="element-1078" data-mp="20ws1476tj7v" />;
      case 'item-list':
        return <ItemList data-id="element-1079" data-mp="21pptfo64ydv" />;
      case 'new-invoice':
        return <NewInvoice onNavigate={selectSection} data-id="element-1080" data-mp="7014c3perx2f" />;
      case 'invoices-draft':
      case 'invoices-sent':
      case 'invoices-unpaid':
      case 'invoices-paid':
      case 'invoices-partial':
      case 'invoices-overdue':
        return <Invoices filter={activeSection} onNavigate={selectSection} data-id="element-1081" data-mp="gpsxqulruk5b" />;
      case 'payments':
      case 'receipts':
        return <Payments data-id="element-1082" data-mp="ku0oexbsia66" />;
      case 'sales-receipts':
        return <SalesReceipts data-id="element-1083" data-mp="ps29fnss0jka" />;
      case 'quotations':
        return <Quotations onNavigate={selectSection} data-id="element-1084" data-mp="uq3pdzuwo2rj" />;
      case 'credit-notes':
        return <CreditNotes onNavigate={selectSection} data-id="element-1085" data-mp="uo105i1kudnv" />;
      case 'reports':
        return <Reports data-id="element-1086" data-mp="2rjyimqrh1hbw" />;
      case 'settings':
        return <Settings data-id="element-1087" data-mp="2wh8dilxmql74" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Billing navigation" data-id="element-1088" data-mp="2wjkxf4my75mt">
      {navigationGroups.map(group => <div key={group.label} data-id="element-1089" data-mp="2ozsgmomf40xz">
          <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${isCollapsed && !mobile ? 'sr-only' : ''}`} data-id="element-1090" data-mp="2k6plwd5wmlg7">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-1091" data-mp="2a9unrvdjmn2g">
            {group.items.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return <button key={item.id} type="button" onClick={() => selectSection(item.id)} title={isCollapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'} ${isCollapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-1092" data-mp="32ys2xpmgk3qz">
                  <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'} ${isCollapsed && !mobile ? '' : 'mr-3'}`} data-id="element-1093" data-mp="17j8wrp1z2r5f" />
                  {(!isCollapsed || mobile) && <>
                      <span className="truncate" data-id="element-1094" data-mp="1v7s86dgbjk1q">{item.label}</span>
                      {item.count !== undefined && item.count > 0 && <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${isActive ? 'bg-blue-100 text-blue-700' : item.id === 'invoices-overdue' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-1095" data-mp="1r5x5b4iqoylu">
                          {item.count}
                        </span>}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  const copy = sectionCopy[activeSection];
  const showNewInvoiceAction = activeSection !== 'new-invoice';
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-1096" data-mp="27r7b5sennfbi">
      <aside className={`hidden shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col ${isCollapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-1097" data-mp="2cpqxhtx5ut4e">
        <div className={`border-b border-slate-100 ${isCollapsed ? 'p-3' : 'p-5'}`} data-id="element-1098" data-mp="1ysc3sczvjz6m">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`} data-id="element-1099" data-mp="1ortdp0hpvmg9">
            <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED MEDICAL CENTRE" className="h-9 w-9 rounded-lg object-contain ring-1 ring-slate-100" data-id="element-1100" data-mp="1tqd012083095" />
            {!isCollapsed && <div className="min-w-0" data-id="element-1101" data-mp="3mnqqlygb3ncq">
                <p className="truncate text-sm font-bold text-slate-900" data-id="element-1102" data-mp="3moryju20ztq8">
                  BMED MEDICAL CENTRE
                </p>
                <p className="truncate text-xs font-medium text-pink-600" data-id="element-1103" data-mp="3hqqizj3t6cji">
                  Billing &amp; Invoicing
                </p>
              </div>}
          </div>
          {!isCollapsed && <div className="mt-4 inline-flex items-center rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700" data-id="element-1104" data-mp="38qbwigf3yref">
              Reception workspace
            </div>}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-1105" data-mp="3vnn7vf0d9jxr">{navigation()}</div>

        <div className="border-t border-slate-100 p-3" data-id="element-1106" data-mp="3rn3aqbfsmgbm">
          {!isCollapsed && <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2" data-id="element-1107" data-mp="rzbrxn45c715">
              <p className="truncate text-xs font-semibold text-slate-700" data-id="element-1108" data-mp="s0mvbltojcrq">
                {currentUser.fullName}
              </p>
              <p className="text-[11px] text-slate-500" data-id="element-1109" data-mp="25vd5dyqr42m4">Live billing access</p>
            </div>}
          <button type="button" onClick={() => setIsCollapsed(value => !value)} className="flex w-full items-center justify-center rounded-md py-2 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={isCollapsed ? 'Expand billing navigation' : 'Collapse billing navigation'} data-id="element-1110" data-mp="2au6i3oy7rpl8">
            {isCollapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-1111" data-mp="2yjiropxgdzo2" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-1112" data-mp="2ymeqegh4ksdk" />}
          </button>
        </div>
      </aside>

      {isMobileNavOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-1113" data-mp="37lrzh81c7exr">
          <button type="button" aria-label="Close billing navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setIsMobileNavOpen(false)} data-id="element-1114" data-mp="3cjthrqqxva86" />
          <aside className="relative flex h-full w-[280px] flex-col bg-white shadow-2xl" data-id="element-1115" data-mp="2tyjit0y9058h">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-1116" data-mp="38mmqqfvupqzx">
              <div className="flex items-center gap-3" data-id="element-1117" data-mp="33nte0poe240t">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED MEDICAL CENTRE" className="h-9 w-9 rounded-lg object-contain ring-1 ring-slate-100" data-id="element-1118" data-mp="33obfc0pwr8fl" />
                <div data-id="element-1119" data-mp="2pwofxs0o5jsl">
                  <p className="text-sm font-bold text-slate-900" data-id="element-1120" data-mp="2pxzmfzli15ne">
                    BMED MEDICAL CENTRE
                  </p>
                  <p className="text-xs font-medium text-pink-600" data-id="element-1121" data-mp="2kzoclov7ev56">
                    Billing &amp; Invoicing
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setIsMobileNavOpen(false)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close billing navigation" data-id="element-1122" data-mp="6ky4bd81e8q">
                <XIcon className="h-5 w-5" data-id="element-1123" data-mp="3uvtejxchj10z" />
              </button>
            </div>
            <div className="px-5 pt-4" data-id="element-1124" data-mp="2r92lrvj6530">
              <span className="inline-flex rounded-md border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700" data-id="element-1125" data-mp="2sags57uf7oy">
                Reception workspace
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1126" data-mp="vfepak65a45h">{navigation(true)}</div>
          </aside>
        </div>}

      <section className="min-w-0 flex-1" data-id="element-1127" data-mp="hmyto4fkp8el">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-1128" data-mp="lplcjfu9nizn">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-1129" data-mp="19eeebsxkkl26">
            <div className="flex min-w-0 items-center gap-3" data-id="element-1130" data-mp="1e7h8o3a67yjf">
              <button type="button" onClick={() => setIsMobileNavOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open billing navigation" data-id="element-1131" data-mp="1o2ehzvp6i807">
                <MenuIcon className="h-5 w-5" data-id="element-1132" data-mp="1a2vghlqvhuwv" />
              </button>
              <div className="min-w-0" data-id="element-1133" data-mp="1a4zks4soovxo">
                <p className="mb-0.5 text-xs font-medium text-slate-500" data-id="element-1134" data-mp="154bwhqvuat75">
                  Reception <span className="mx-1 text-slate-300" data-id="element-1135" data-mp="10d3isinh8lo6">/</span>{' '}
                  Billing
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-1136" data-mp="waytfw42ab7c">
                  <h1 className="truncate text-lg font-bold text-slate-900 sm:text-xl" data-id="element-1137" data-mp="rcdue00jpxkh">
                    {copy.title}
                  </h1>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-1138" data-mp="mc9dw9zn12u9">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-1139" data-mp="hfiupdsjdr4m" />{' '}
                    Live data
                  </span>
                </div>
                <p className="hidden text-sm text-slate-500 xl:block" data-id="element-1140" data-mp="c942linfgxim">
                  {copy.subtitle}
                </p>
              </div>
            </div>
            {showNewInvoiceAction && <button type="button" onClick={() => selectSection('new-invoice')} className="inline-flex shrink-0 items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-1141" data-mp="m83agz0dcp4k">
                <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1142" data-mp="3ulipam5ilq53" />
                <span className="hidden sm:inline" data-id="element-1143" data-mp="39h7felakny0">New Invoice</span>
                <span className="sm:hidden" data-id="element-1144" data-mp="31z7opbhwrp8a">Invoice</span>
              </button>}
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-1145" data-mp="36whl5b3pt15m">
          {renderContent()}
        </main>
      </section>
    </div>;
}